import { createApp } from 'vue'
import { createPinia } from 'pinia'
import router from './router'
import App from './App.vue'
import { useAuthStore } from './stores/auth.store'
import { createAuthService } from './services/auth-service.factory'

import './styles/main.css'

import * as Sentry from '@sentry/vue'
import { reportError, handleRouterError } from './lib/error-reporting'

// Create app instance
const app = createApp(App)
const pinia = createPinia()

// Initialise Sentry as early as possible — reads config from VITE_SENTRY_* env vars.
// VITE_SENTRY_ENABLED=true is injected ONLY by the stable branch GitHub Actions deploy.
// This means Sentry is completely silent in dev, test, and CI — production only.
const sentryEnabled = import.meta.env.VITE_SENTRY_ENABLED === 'true'

if (sentryEnabled && import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    app,
    dsn: import.meta.env.VITE_SENTRY_DSN as string,
    environment: (import.meta.env.VITE_SENTRY_ENVIRONMENT as string) || 'development',

    // Automatic instrumentation: Vue Router tracing + performance
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      // Session Replay — captures user interactions for error context
      Sentry.replayIntegration({
        // Mask text and block media by default so no PII is captured in replays
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],

    // Tracing — production only so always use production sample rates
    tracesSampleRate: 0.2,

    // Propagate trace headers to BFF API and Site Compass API endpoints
    tracePropagationTargets: [/\/api\//, /^https:\/\/.*\.sitecompass\.com\.au\/api/],

    // Session Replay
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,

    // Structured logs (requires enableLogs on Sentry SDK)
    enableLogs: true,

    // Capture default PII (IP address etc.) — review privacy policy before enabling in prod
    sendDefaultPii: false,
  })
}

// Global Vue error handler — guarantees render/lifecycle/watcher errors reach Sentry
// (set after Sentry.init so this is the active handler; reportError still captures to Sentry).
app.config.errorHandler = (err, _instance, info) => {
  reportError(err, { where: 'vue:errorHandler', extra: { lifecycleHook: info } })
}

// IMPORTANT: Register Pinia and Router BEFORE mounting
// This ensures stores and router are available when components initialize
app.use(pinia)
app.use(router)

// Navigation progress indicator — registered before authGuard so the indicator
// fires at the very start of every navigation, including during auth checks.
import { useNavigationStore } from './stores/navigation.store'
router.beforeEach(() => {
  useNavigationStore().startNavigation()
})
router.afterEach(() => {
  useNavigationStore().endNavigation()
  // A successful navigation means chunks loaded fine — reset the stale-chunk reload guard
  // so a future deploy can trigger a fresh one-time recovery reload.
  try {
    sessionStorage.removeItem('sc-chunk-reloaded')
  } catch {
    /* sessionStorage unavailable (private mode) — non-fatal */
  }
})
router.onError(error => {
  useNavigationStore().endNavigation()
  handleRouterError(error)
})

// Register auth guard after Pinia is initialized
import { authGuard } from './router/guards/auth.guard'
router.beforeEach(authGuard)

// Wait for router to be ready before initializing auth
router.isReady().then(() => {
  // Initialize auth service (now async)
  createAuthService()
    .then(authService => {
      const authStore = useAuthStore()
      authStore.setAuthService(authService)

      // Initialize auth state
      return authStore.initialize()
    })
    .then(() => {
      // Mount app only after everything is ready
      app.mount('#app')
    })
    .catch(error => {
      console.error('Auth initialization error:', error)
      app.config.globalProperties.$authInitError = error
      // Still mount the app to show error state
      app.mount('#app')
    })
})
